'use client'

import { noop } from 'lodash'
import { Loader } from '@vinted/web-ui'

import { useState } from 'react'

import { Grid as ItemGrid } from '@marketplace-web/common-components/grid-ui'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { ProductItem } from '@marketplace-web/item-box/item-box-feature'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'

import { ErrorBoundary } from '@marketplace-web/error-display/error-display-util'
import { ComponentError } from '@marketplace-web/error-display/error-display-feature'

import { getIsBundlingAllowed } from '../../pages/BundleItems/utils'
import BundleItemFooter from '../BundleItemFooter/BundleItemFooter'
import BundlingPolicyDialog from '../BundlingPolicyDialog/BundlingPolicyDialog'
import { useItemSelection } from '../../hooks'

type Props = {
  items: Array<ProductItemModel>
  selectedItems: Array<ProductItemModel>
  isLoading: boolean
  endReached?: boolean
  emptyState?: JSX.Element
  loadItems?: () => void
  updateSelectedItems: (items: Array<ProductItemModel>) => void
  viewingSelf?: boolean
  showFavourite?: boolean
  showPriceBreakdown?: boolean
  selectionLimit?: number
  onItemVisible?: React.ComponentProps<typeof ItemGrid<ProductItemModel>>['onItemVisible']
  onItemClick?: (item: ProductItemModel, position: number, isSelected: boolean) => void
  bundleTrackingId: string | null
  sellerId: number
  disabled: boolean
}

const ItemsMultiselect = ({
  items,
  selectedItems,
  isLoading,
  endReached,
  emptyState,
  loadItems,
  updateSelectedItems,
  viewingSelf = false,
  showFavourite = false,
  showPriceBreakdown,
  selectionLimit,
  onItemVisible,
  onItemClick,
  bundleTrackingId,
  sellerId,
  disabled = false,
}: Props) => {
  const [bundlingPolicyDialogUi, setBundlingPolicyDialogUi] = useState<{
    isOpen: boolean
    selectedItemId?: number
  }>({ isOpen: false })

  const { selectItem, isLimitReached } = useItemSelection({
    selectedItems,
    updateSelectedItems,
    selectionLimit,
    onItemClick,
    items,
    sellerId,
    bundleTrackingId,
    screen: 'bundling',
  })

  const renderItem: React.ComponentProps<typeof ItemGrid<ProductItemModel>>['renderItem'] = (
    { data: item },
    index,
  ) => {
    return (
      <ErrorBoundary FallbackComponent={ComponentError}>
        <ProductItem
          item={item}
          renderFooter={
            <BundleItemFooter
              item={item}
              items={items}
              selectedItems={selectedItems}
              itemIndex={index}
              onClick={selectItem}
              sellerId={sellerId}
              bundleTrackingId={bundleTrackingId}
              isSelectedItemLimitReached={() => isLimitReached()}
              onCantBundleButtonClick={() =>
                setBundlingPolicyDialogUi({ isOpen: true, selectedItemId: item.id })
              }
              screen="bundling"
              disabled={disabled}
            />
          }
          showStatus
          viewingSelf={viewingSelf}
          showFavourite={showFavourite}
          showPriceBreakdown={showPriceBreakdown}
          onClick={
            getIsBundlingAllowed({
              item,
              bundleItems: items,
              firstSelectedItemId: selectedItems[0]?.id,
            })
              ? selectItem(item, index)
              : noop
          }
          isUrlDisabled
        />
      </ErrorBoundary>
    )
  }

  const renderItemsContainer = () => {
    const gridItems = items.map(item => ({ id: item.id, data: item }))

    return <ItemGrid items={gridItems} renderItem={renderItem} onItemVisible={onItemVisible} />
  }

  return (
    <>
      {!loadItems && renderItemsContainer()}
      {isLoading && (
        <div className="u-flexbox u-justify-content-center">
          <Loader size="large" testId="items-multiselect--loader" />
        </div>
      )}
      {loadItems && (
        <InfiniteScroll
          renderItems={renderItemsContainer}
          onPageEnd={loadItems}
          cursor={String(items.length)}
        />
      )}
      {!isLoading && endReached && !items.length && (
        <div className="u-flexbox u-justify-content-center u-flex-grow">{emptyState}</div>
      )}
      <BundlingPolicyDialog
        show={bundlingPolicyDialogUi.isOpen}
        bundleTrackingId={bundleTrackingId}
        sellerId={sellerId}
        selectedItemId={bundlingPolicyDialogUi.selectedItemId}
        onClose={() => setBundlingPolicyDialogUi({ isOpen: false })}
      />
    </>
  )
}

export default ItemsMultiselect
